import { useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import cs from 'classnames';

import { handleAction } from 'components/practice/charts/ClinicalNote/Orders/lib/handleActionMethods';
import Breadcrumb from 'components/shared/Breadcrumb/Breadcrumb';
import useBreadcrumb from 'components/shared/Breadcrumb/useBreadcrumb';
import ErrorMessage from 'components/shared/ErrorMessage/ErrorMessage';
import Icon from 'components/shared/Icon/Icon';
import Modal from 'components/shared/Modal/Modal';
import Skeleton from 'components/shared/Skeleton/Skeleton';

import Footer from './components/Footer';
import Head from './components/Head';
import useActionViews from './hooks/useActionViews';
import useOrder from './hooks/useOrder';
import { routes } from './lib/breadcrumbHelper';

const OrdersModal = ({
  modalOpen = false,
  handleClose = () => null,
  navigateToParams = null,
  setNavigateToParams = () => null,
  fromPatient
}) => {
  const [activeView, setActiveView] = useState({
    type: 'initial',
    component: 'list',
    loading: false
  });

  const breadcrumb = useBreadcrumb([routes?.[activeView?.component]?.[activeView?.type]]);

  const { setSteps, steps, step, push } = breadcrumb;

  const actionRef = useRef();
  const printRef = useRef();

  const handleActionProps = { ...activeView, breadcrumb, setActiveView, actionRef };

  const id = activeView?.data?.id;
  const type = activeView?.data?.type;
  const resultId = activeView?.data?.resultId;

  const { order, loading } = useOrder({ type, id, fromPatient });

  const { actionViews } = useActionViews({
    handleActionProps,
    activeView,
    handleClose,
    setActiveView
  });

  useEffect(() => {
    const [type, component] = steps[step].name.split('-');
    if (activeView.component !== component || activeView.type !== type) {
      setActiveView((prev) => ({ ...prev, type, component }));
    }
  }, [step]);

  useEffect(() => {
    const newStep = routes?.[activeView?.component]?.[activeView?.type];
    if (newStep) push(newStep);
  }, [activeView?.component, activeView?.type]);

  useEffect(() => {
    if (type && order) {
      if (!resultId) {
        setActiveView((prev) => ({ ...prev, loading, type, itemProps: order }));
      } else {
        setActiveView((prev) => ({ ...prev, loading, itemProps: order }));
      }
    }
  }, [order, loading, type]);

  useEffect(() => {
    if (navigateToParams) {
      if (navigateToParams?.data?.resultId) navigateToResult();
      else navigateToEditData();
    }
  }, [navigateToParams]);

  const navigateToEditData = () => {
    const type = navigateToParams?.type || navigateToParams?.data?.type || activeView?.type;
    const component = navigateToParams?.component || 'preview';

    setSteps([routes?.[component]?.[type]]);
    setActiveView((prevState) => ({
      ...prevState,
      ...navigateToParams,
      component: navigateToParams?.component || 'preview'
    }));

    setNavigateToParams(null);
  };

  const navigateToResult = () => {
    setSteps([routes.preview.elab_order, routes.list.results, routes.preview.results]);

    setActiveView((prevState) => ({
      ...prevState,
      ...navigateToParams,
      type: 'results',
      component: 'preview'
    }));

    setNavigateToParams(null);
  };

  const itemPropsId =
    activeView?.itemProps?.id ??
    activeView?.itemProps?.labOrder?.id ??
    activeView?.itemProps?.eLabOrder?.id;

  const isELabOrder = activeView.type === 'elab_order';

  const currentView = () => {
    const type = activeView?.type || '';
    const view = activeView?.component || '';

    return actionViews[type]?.components[view];
  };

  const backHandler = () => handleAction({ ...handleActionProps, method: 'back' });

  const BackButton = () => {
    if (step === 0) return null;

    return (
      <div
        onClick={backHandler}
        className="!mr-3 cursor-pointer items-center rounded-lg !p-1 transition-all hover:bg-neutral-100">
        <Icon color="black" stroke icon="arrow-left-linear" className="cursor-pointer" />
      </div>
    );
  };

  return (
    <Modal
      isLarge={isELabOrder && activeView.component !== 'preview'}
      isOpen={modalOpen}
      handleClose={handleClose}
      shouldCloseOnOverlayClick={activeView.component !== 'create'}
      bodyClassName={cs(activeView.type === 'results' && 'p-0')}
      customHeader={
        loading ? (
          <div className="w-full">
            <Skeleton height="100%" />
          </div>
        ) : (
          <div className="Modal__head__left w-full">
            <BackButton />

            <Breadcrumb breadcrumb={breadcrumb} />

            <Head
              itemPropsId={itemPropsId}
              activeView={activeView}
              setActiveView={setActiveView}
              printRef={printRef}
              openResultsModal={() => {
                setActiveView((prev) => ({ ...prev, type: 'results', component: 'list' }));
              }}
              actionViews={actionViews}
              fromPatient={fromPatient}
            />
          </div>
        )
      }
      className={cs(
        'h-full !bg-white transition-[width] sm:w-full',
        isELabOrder && activeView.component !== 'preview'
          ? 'xxl:w-[96%] xxl:max-w-[96%]'
          : 'w-[924px]'
      )}
      footer={
        <Footer
          handleClose={handleClose}
          activeView={activeView}
          isELabOrder={isELabOrder}
          handleActionProps={handleActionProps}
          fromPatient={fromPatient}
          orderLoading={loading}
        />
      }>
      <ErrorBoundary FallbackComponent={ErrorMessage}>
        {loading ? (
          <Skeleton height="100%" />
        ) : (
          <div
            id="referral_order"
            className="h-full break-normal print:!bg-white print:!pt-10"
            ref={printRef}>
            {currentView()}
          </div>
        )}
      </ErrorBoundary>
    </Modal>
  );
};

export default OrdersModal;
